import {
  Card,
  CardBody,
  ContextualBanner,
  Input,
  Toggle,
  Tooltip,
} from '@hawkins/components';
import { useState, useEffect } from 'react';
import { ceiling } from 'src/domains/calculators/utilities/ceiling';
import { roundToNearestEven } from 'src/domains/calculators/utilities/round';
import { HorizontalInputStack } from './HorizontalInputStack';
import styles from './CustomFramingDecisionInput.module.css';
import { ResolutionInput } from '../FramingChart/types';

type CustomFramingDecisionInputProps = {
  customFramingDecision: ResolutionInput;
  lensSqueezeFactor: number;
  lockedAspectRatio?: number;
  onAspectRatioToggle: (locked: boolean) => void;
  setCustomFramingDecision: (resolution: ResolutionInput) => void;
  setLockAspectRatio: (locked: boolean) => void;
};

export const CustomFramingDecisionInput = ({
  customFramingDecision,
  lensSqueezeFactor,
  lockedAspectRatio,
  onAspectRatioToggle,
  setCustomFramingDecision,
  setLockAspectRatio,
}: CustomFramingDecisionInputProps) => {
  const customWidth = customFramingDecision.width;
  const customHeight = customFramingDecision.height;

  const hasCustomFramingDecision = !!customWidth && !!customHeight;

  const [
    showCustomFramingDecisionInputs,
    setShowCustomFramingDecisionInputs,
  ] = useState<boolean>(hasCustomFramingDecision);

  const [inputWidth, setInputWidth] = useState(
    customWidth?.toString() ?? ''
  );
  const [inputHeight, setInputHeight] = useState(
    customHeight?.toString() ?? ''
  );

  const [invalidWidth, setInvalidWidth] = useState(false);
  const [invalidHeight, setInvalidHeight] = useState(false);

  useEffect(() => {
    setInputWidth(customWidth?.toString() ?? '');
  }, [customWidth]);

  useEffect(() => {
    setInputHeight(customHeight?.toString() ?? '');
  }, [customHeight]);

  const calculateWidth = (
    height: number,
    aspectRatio: number,
    lensSqueezeFactor: number
  ) => roundToNearestEven((height * aspectRatio) / lensSqueezeFactor);

  const calculateHeight = (
    width: number,
    aspectRatio: number,
    lensSqueezeFactor: number
  ) => roundToNearestEven((width * lensSqueezeFactor) / aspectRatio);

  const roundingMessage =
    'Value is rounded up to the nearest even number';

  return (
    <>
      <Toggle
        aria-label="Toggle Custom Framing Decision Input"
        checked={showCustomFramingDecisionInputs}
        label="Custom Framing Decision"
        onChange={(_event, checked) => {
          setShowCustomFramingDecisionInputs(checked);
          setCustomFramingDecision({ width: null, height: null });
          setLockAspectRatio(false);
          // Clear input values and validation errors
          setInputWidth('');
          setInputHeight('');
          setInvalidWidth(false);
          setInvalidHeight(false);
        }}
      />

      {showCustomFramingDecisionInputs && (
        <>
          {lockedAspectRatio && (
            <ContextualBanner
              compact
              text={`Values are constrained to ${lockedAspectRatio}:1 aspect ratio. Changing one dimension will automatically update the other.`}
              variant="warning"
            />
          )}
          <Card>
            <CardBody
              className={styles.customFramingDecisionInputContainer}
            >
              <HorizontalInputStack>
                <Tooltip label={roundingMessage} placement="bottom">
                  <Input
                    validationStatus={
                      invalidWidth ? 'error' : undefined
                    }
                    validationMessage={
                      invalidWidth ? 'Invalid width' : undefined
                    }
                    label="Width"
                    type="number"
                    fullWidth
                    value={inputWidth}
                    onChange={(event) => {
                      const value = event.target.value;
                      setInputWidth(value);
                      // Set validation state for input
                      const width = Number(value);
                      setInvalidWidth(value === '' || width <= 0);
                    }}
                    onBlur={() => {
                      const rawWidth = Number(inputWidth);
                      if (rawWidth > 0) {
                        const roundedWidth = ceiling(rawWidth, 2);
                        setInputWidth(roundedWidth.toString());
                        if (lockedAspectRatio) {
                          const height = calculateHeight(
                            roundedWidth,
                            lockedAspectRatio,
                            lensSqueezeFactor
                          );
                          setCustomFramingDecision({
                            width: roundedWidth,
                            height,
                          });
                          setInputHeight(height.toString());
                          // Clear height validation error since we calculated a valid value
                          setInvalidHeight(false);
                        } else {
                          setCustomFramingDecision({
                            width: roundedWidth,
                            height: customHeight, // keep height unchanged
                          });
                        }
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip label={roundingMessage} placement="bottom">
                  <Input
                    validationStatus={
                      invalidHeight ? 'error' : undefined
                    }
                    validationMessage={
                      invalidHeight ? 'Invalid height' : undefined
                    }
                    label="Height"
                    type="number"
                    fullWidth
                    value={inputHeight}
                    onChange={(event) => {
                      const value = event.target.value;
                      setInputHeight(value);

                      // Set validation state for input
                      const height = Number(value);
                      setInvalidHeight(value === '' || height <= 0);
                    }}
                    onBlur={() => {
                      const rawHeight = Number(inputHeight);
                      if (rawHeight > 0) {
                        const roundedHeight = ceiling(rawHeight, 2);
                        setInputHeight(roundedHeight.toString());
                        if (lockedAspectRatio) {
                          const width = calculateWidth(
                            roundedHeight,
                            lockedAspectRatio,
                            lensSqueezeFactor
                          );
                          setCustomFramingDecision({
                            width,
                            height: roundedHeight,
                          });
                          setInputWidth(width.toString());
                          // Clear width validation error since we calculated a valid value
                          setInvalidWidth(false);
                        } else {
                          setCustomFramingDecision({
                            height: roundedHeight,
                            width: customWidth, // keep width unchanged
                          });
                        }
                      }
                    }}
                  />
                </Tooltip>
              </HorizontalInputStack>
              {onAspectRatioToggle && (
                <div className={styles.lockAspectRatioToggle}>
                  <Toggle
                    label="Lock Aspect Ratio"
                    checked={!!lockedAspectRatio}
                    onChange={(_event, checked) => {
                      onAspectRatioToggle(checked);
                    }}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
};
